import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { PageWrapper } from '../../components';
import venueListsApiService from '../../services/venueListsApi';
import './VenueLists.css';

const formatDate = (value) => {
  if (!value) return 'No event date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const VenueLists = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const [listIdToOpen, setListIdToOpen] = useState('');
  const [newList, setNewList] = useState({
    title: '',
    description: '',
    event_date: '',
    event_name: ''
  });

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  const loadLists = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await venueListsApiService.listVenueLists({ scope: 'mine' });
      setLists(Array.isArray(data) ? data : []);
    } catch (loadError) {
      if (loadError?.message === 'SESSION_EXPIRED') {
        setError('Session expired. Please sign in again.');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        navigate('/', { replace: true });
        return;
      }
      setError(loadError?.message || 'Failed to load venue lists.');
      setLists([]);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!currentUser) return;
    loadLists();
  }, [currentUser, loadLists]);

  const groupedLists = useMemo(() => {
    const buckets = {
      active: [],
      locked: [],
      archived: []
    };
    lists.forEach((list) => {
      const normalizedStatus = String(list?.status || 'active').toLowerCase();
      if (normalizedStatus === 'locked') {
        buckets.locked.push(list);
      } else if (normalizedStatus === 'archived') {
        buckets.archived.push(list);
      } else {
        buckets.active.push(list);
      }
    });
    return buckets;
  }, [lists]);

  const handleCreateList = async (event) => {
    event.preventDefault();
    if (!newList.title.trim()) {
      setError('List title is required.');
      return;
    }

    try {
      setCreating(true);
      setError('');
      const payload = {
        title: newList.title.trim(),
        status: 'active',
        ...(newList.description.trim() ? { description: newList.description.trim() } : {}),
        ...(newList.event_date ? { event_date: newList.event_date } : {}),
        ...(newList.event_name.trim() ? { event_name: newList.event_name.trim() } : {})
      };
      const created = await venueListsApiService.createVenueList(payload);
      const createdId = created?.id || created?.venue_list?.id || null;
      setNewList({ title: '', description: '', event_date: '', event_name: '' });
      if (createdId) {
        navigate(`/venue-lists/${createdId}`);
        return;
      }
      await loadLists();
    } catch (createError) {
      if (createError?.message === 'SESSION_EXPIRED') {
        setError('Session expired. Please sign in again.');
        localStorage.removeItem('access_token');
        localStorage.removeItem('user');
        navigate('/', { replace: true });
        return;
      }
      setError(createError?.message || 'Failed to create venue list.');
    } finally {
      setCreating(false);
    }
  };

  const handleOpenListById = (event) => {
    event.preventDefault();
    const trimmed = listIdToOpen.trim();
    if (!trimmed) {
      setError('Enter a list ID to open.');
      return;
    }
    navigate(`/venue-lists/${trimmed}`);
  };

  if (!currentUser) {
    return null;
  }

  return (
    <PageWrapper useNewFooter useLanding2Header>
      <section className="venue-lists-page">
        <div className="venue-lists-shell">
          <div className="venue-lists-header">
            <h1>Venue lists</h1>
            <p>Create shortlist lists, invite teammates, and vote on venues.</p>
            <p className="venue-lists-scope-note">
              This page shows lists where you are owner or accepted member.
            </p>
          </div>

          {error ? <div className="venue-lists-error">{error}</div> : null}

          <form className="venue-lists-open-by-id" onSubmit={handleOpenListById}>
            <input
              type="text"
              value={listIdToOpen}
              onChange={(e) => setListIdToOpen(e.target.value)}
              placeholder="Open list by ID (UUID)"
            />
            <button type="submit">Open</button>
          </form>

          <form className="venue-lists-create-card" onSubmit={handleCreateList}>
            <h2>Create a new list</h2>
            <div className="venue-lists-create-grid">
              <label>
                <span>List title *</span>
                <input
                  type="text"
                  value={newList.title}
                  onChange={(e) => setNewList((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Q4 Launch Event Options"
                  required
                />
              </label>
              <label>
                <span>Event name</span>
                <input
                  type="text"
                  value={newList.event_name}
                  onChange={(e) => setNewList((prev) => ({ ...prev, event_name: e.target.value }))}
                  placeholder="Q4 product launch"
                />
              </label>
              <label>
                <span>Event date</span>
                <input
                  type="date"
                  value={newList.event_date}
                  onChange={(e) => setNewList((prev) => ({ ...prev, event_date: e.target.value }))}
                />
              </label>
              <label className="venue-lists-create-full">
                <span>Description</span>
                <textarea
                  rows={3}
                  value={newList.description}
                  onChange={(e) => setNewList((prev) => ({ ...prev, description: e.target.value }))}
                  placeholder="What is this shortlist for?"
                />
              </label>
            </div>
            <div className="venue-lists-create-actions">
              <button type="submit" disabled={creating}>
                {creating ? 'Creating...' : 'Create list'}
              </button>
            </div>
          </form>

          <div className="venue-lists-groups">
            {loading ? <p className="venue-lists-note">Loading your lists...</p> : null}
            {!loading && lists.length === 0 ? (
              <p className="venue-lists-note">No lists yet. Create your first shortlist above.</p>
            ) : null}

            {!loading &&
              ['active', 'locked', 'archived'].map((status) => {
                const items = groupedLists[status];
                if (!items || items.length === 0) return null;
                return (
                  <section key={status}>
                    <h3 className="venue-lists-status-heading">{status}</h3>
                    <div className="venue-lists-grid">
                      {items.map((list) => {
                        const listId = list?.id || list?.list_id;
                        const itemCount =
                          list?.item_count ??
                          (Array.isArray(list?.items) ? list.items.length : 0);
                        const memberCount =
                          list?.member_count ??
                          (Array.isArray(list?.members) ? list.members.length : 0);
                        return (
                          <Link
                            className="venue-list-card"
                            key={listId}
                            to={listId ? `/venue-lists/${listId}` : '/venue-lists'}
                          >
                            <div className="venue-list-card-top">
                              <h4>{list?.title || 'Untitled list'}</h4>
                              <span className={`venue-list-status venue-list-status--${String(list?.status || 'active').toLowerCase()}`}>
                                {String(list?.status || 'active')}
                              </span>
                            </div>
                            <p className="venue-list-description">
                              {list?.description || 'No description'}
                            </p>
                            <div className="venue-list-meta">
                              <span>{itemCount} venues</span>
                              <span>{memberCount} members</span>
                              <span>{formatDate(list?.event_date)}</span>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  </section>
                );
              })}
          </div>
        </div>
      </section>
    </PageWrapper>
  );
};

export default VenueLists;
